import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, ExternalLink, MessageCircle, Package, PlayCircle } from 'lucide-react';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import {
  TEXTO_PRODUTO_GENERICO,
  destaqueParaVitrine,
  getItemDestaquePorSlug,
  imagemProduto,
  isProdutoUuid,
  LINKS_EXTERNOS,
} from '../data/catalogoDestaque';
import { hasSupabasePublicConfig, supabase } from '../lib/supabase';
import type { ItemDestaque } from '../data/catalogoDestaque';

type Row = {
  id: string;
  nome: string;
  descricao?: string;
  preco_venda: number;
  categoria?: string;
};

const WA = 'https://wa.me/5567992089769?text=';

function mensagemAmigavelErroCarregamento(raw: string): string {
  const t = raw.toLowerCase();
  if (t.includes('jwt') || t.includes('failed to fetch') || t.includes('network') || t.length > 100) {
    return 'Não foi possível carregar este produto agora. Tente de novo em instantes ou fale com a Lunins pelo WhatsApp.';
  }
  return raw;
}

export default function Produto() {
  const { id } = useParams<{ id: string }>();
  const [p, setP] = useState<Row | null>(null);
  const [detalhe, setDetalhe] = useState<ItemDestaque | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [cupomCodigo, setCupomCodigo] = useState('');
  const [cupomMsg, setCupomMsg] = useState<string | null>(null);
  const [cupomLoading, setCupomLoading] = useState(false);
  const [precoComCupom, setPrecoComCupom] = useState<number | null>(null);

  useEffect(() => {
    if (!id) return;

    if (!isProdutoUuid(id)) {
      const item = getItemDestaquePorSlug(id);
      if (!item) {
        setErr('Produto não encontrado.');
        return;
      }
      setP(destaqueParaVitrine(item) as Row);
      setDetalhe(item);
      setErr(null);
      return;
    }

    const fallbackByKnownUuid: Record<string, string> = {
      'ed4ad403-255f-43c0-9f0a-4185e0d26882': 'assento-shiatsu-premium',
      'fd86b624-0b73-4bd5-b604-d8f57cf873e5': 'cinta-fisiopower',
      'f6219498-90f2-40ce-8d98-560085e0262e': 'colete-shiatsu',
      '9f399c0e-6275-4706-962a-ee422d136f13': 'esteira-vibro-neck-shoulder',
      '4f1810e4-6073-4523-b20b-d5ced2b0d20f': 'massageador-pes-shiatsu-pro',
      '4691faca-17c3-490d-bb86-a679bbacbf87': 'massageador-manual',
      '1d1ea836-29c0-45a4-806e-b7ea79fd235e': 'massageador-relax-confort-sem-fio',
      'f1b9ec48-aae8-4683-86b3-034e1b573f9f': 'plataforma-reflexologia',
      '2dc7bcb1-932d-42e0-a410-3b0667b8c982': 'plataforma-reflexologia',
    };

    const slug = fallbackByKnownUuid[id];
    if (slug) {
      const item = getItemDestaquePorSlug(slug);
      if (item) {
        setP(destaqueParaVitrine(item) as Row);
        setDetalhe(item);
        setErr(null);
        return;
      }
    }

    setErr('Produto não encontrado.');
  }, [id]);

  if (err) {
    return (
      <div className="flex min-h-screen flex-col">
        <SiteHeader />
        <main className="mx-auto flex flex-1 max-w-6xl flex-col items-center justify-center px-4 py-20 text-center">
          <p role="alert" className="text-red-700">
            {mensagemAmigavelErroCarregamento(err)}
          </p>
          <Link to="/loja" className="mt-6 text-lunins-accent hover:underline">
            Voltar à loja
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (!p) {
    return (
      <div className="flex min-h-screen flex-col">
        <SiteHeader />
        <main className="mx-auto flex w-full max-w-6xl flex-1 flex-col px-4 py-12">
          <div className="h-96 animate-pulse rounded-2xl bg-lunins-elevated/50" />
        </main>
        <SiteFooter />
      </div>
    );
  }

  const lista = Number(p.preco_venda);
  const eff = precoComCupom != null ? precoComCupom : lista;
  const price = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(lista);
  const priceEff = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(eff);
  const waMsg = encodeURIComponent(
    `Olá! Tenho interesse no produto: ${p.nome}${precoComCupom != null ? ` (cupom, valor ref. ${priceEff})` : ''}`
  );

  const validarCupom = async () => {
    setCupomMsg(null);
    const c = cupomCodigo.trim();
    if (!c) {
      setCupomMsg('Informe o código.');
      return;
    }
    if (!hasSupabasePublicConfig) {
      setCupomMsg('Cupom indisponível neste ambiente (configure Supabase na vitrine).');
      return;
    }
    setCupomLoading(true);
    try {
      const { data, error } = await supabase.rpc('validar_cupom_vitrine', {
        p_codigo: c,
        p_preco_lista: lista,
      });
      if (error) {
        setCupomMsg(error.message);
        setPrecoComCupom(null);
        return;
      }
      const row = data as { ok?: boolean; error?: string; preco_com_desconto?: number };
      if (!row?.ok) {
        setCupomMsg('Cupom inválido ou expirado.');
        setPrecoComCupom(null);
        return;
      }
      setPrecoComCupom(Number(row.preco_com_desconto));
      setCupomMsg('Cupom aplicado: 40% sobre o preço de tabela deste produto.');
    } finally {
      setCupomLoading(false);
    }
  };
  const imgSrc = detalhe ? imagemProduto(detalhe) : undefined;

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />

      <main className="flex-1">
        <div className="border-b border-lunins-border bg-lunins-sand/40">
          <div className="mx-auto max-w-6xl px-4 py-4 text-sm text-lunins-muted">
            <Link to="/" className="hover:text-lunins-ink">
              Início
            </Link>
            <ChevronRight className="mx-1 inline h-4 w-4" />
            <Link to="/loja" className="hover:text-lunins-ink">
              Loja
            </Link>
            <ChevronRight className="mx-1 inline h-4 w-4" />
            <span className="text-lunins-ink">{p.nome}</span>
          </div>
        </div>

        <div className="mx-auto max-w-6xl px-4 py-10 md:py-16">
          <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
            <div className="relative flex aspect-square max-h-[min(520px,85vw)] items-center justify-center overflow-hidden rounded-3xl border border-lunins-border bg-lunins-elevated lg:max-h-none">
              {imgSrc ? (
                <img
                  src={imgSrc}
                  alt={p.nome}
                  className="relative h-full w-full object-contain p-5"
                  loading="eager"
                />
              ) : (
                <div className="relative flex h-full items-center justify-center">
                  <Package className="h-28 w-28 text-lunins-accent/70 md:h-36 md:w-36" strokeWidth={1} />
                </div>
              )}
            </div>

            <div>
              {p.categoria && (
                <span className="inline-flex rounded-md bg-lunins-sand px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-lunins-muted">
                  {String(p.categoria)}
                </span>
              )}
              <h1 className="mt-4 font-display text-3xl font-semibold leading-tight text-lunins-ink md:text-4xl">{p.nome}</h1>
              {p.descricao && <p className="mt-4 text-lg text-lunins-muted">{p.descricao}</p>}
              <p className="mt-8 font-display text-4xl font-semibold text-lunins-ink md:text-5xl">
                {precoComCupom != null ? (
                  <>
                    <span className="text-lunins-muted line-through decoration-lunins-muted/60">{price}</span>{' '}
                    <span className="text-lunins-teal">{priceEff}</span>
                  </>
                ) : (
                  price
                )}
              </p>
              {hasSupabasePublicConfig && (
                <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-end">
                  <div className="flex-1">
                    <label className="text-xs font-medium text-lunins-muted">Cupom (próxima compra / pós-venda)</label>
                    <input
                      type="text"
                      value={cupomCodigo}
                      onChange={(e) => setCupomCodigo(e.target.value)}
                      placeholder="Ex.: LUNINS-XXXXXXXX"
                      className="mt-1 w-full rounded-xl border border-lunins-border bg-lunins-surface px-3 py-2 text-sm text-lunins-ink placeholder:text-lunins-muted"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={validarCupom}
                    disabled={cupomLoading}
                    className="rounded-xl border border-lunins-ink/20 bg-lunins-sand px-4 py-2 text-sm font-semibold text-lunins-ink transition hover:bg-lunins-border disabled:opacity-50"
                  >
                    {cupomLoading ? '…' : 'Validar'}
                  </button>
                </div>
              )}
              {cupomMsg && <p className="mt-2 text-xs text-lunins-muted">{cupomMsg}</p>}
              <p className="mt-4 text-sm text-lunins-muted">
                Valores e condições de parcelamento conforme campanha e meio de pagamento — confirme com a equipe.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  href={`${WA}${waMsg}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-lunins-ink px-6 py-4 text-center text-base font-semibold text-lunins-cream transition hover:bg-lunins-ink-soft"
                >
                  <MessageCircle className="h-5 w-5" />
                  Falar no WhatsApp
                </a>
                <Link
                  to="/loja"
                  className="lunins-btn-secondary flex-1 py-4"
                >
                  Voltar ao catálogo
                </Link>
              </div>

              <a
                href={LINKS_EXTERNOS.instagramReels}
                target="_blank"
                rel="noreferrer"
                className="mt-6 flex items-center gap-3 rounded-2xl border border-lunins-border bg-lunins-sand/50 px-5 py-4 text-sm font-semibold text-lunins-ink transition hover:border-lunins-gold/40"
              >
                <PlayCircle className="h-6 w-6 shrink-0" />
                <span>
                  Tutoriais em vídeo no Instagram — busque pelo nome do aparelho ou categoria nos reels @luninsbr
                </span>
                <ExternalLink className="h-4 w-4 shrink-0 opacity-70" />
              </a>
            </div>
          </div>

          {/* Copy longa */}
          <div className="mx-auto mt-14 max-w-3xl border-t border-lunins-border pt-12">
            {detalhe ? (
              <>
                <h2 className="font-display text-xl font-semibold text-lunins-ink md:text-2xl">Sobre este equipamento</h2>
                <p className="mt-6 text-base leading-relaxed text-lunins-muted">{detalhe.descricaoLonga}</p>
                <h3 className="mt-10 font-display text-lg font-semibold text-lunins-ink">Benefícios e diferenciais</h3>
                <ul className="mt-4 space-y-3">
                  {detalhe.beneficios.map((b) => (
                    <li key={b} className="flex gap-3 text-sm leading-relaxed text-lunins-muted">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-lunins-gold" />
                      {b}
                    </li>
                  ))}
                </ul>
                <h3 className="mt-10 font-display text-lg font-semibold text-lunins-ink">Indicado para</h3>
                <p className="mt-4 text-base leading-relaxed text-lunins-muted">{detalhe.indicadoPara}</p>
                {detalhe.cuidados && (
                  <>
                    <h3 className="mt-10 font-display text-lg font-semibold text-lunins-ink">Cuidados e boas práticas</h3>
                    <p className="mt-4 text-base leading-relaxed text-lunins-muted">{detalhe.cuidados}</p>
                  </>
                )}
                {detalhe.fabricanteReferencia && (
                  <p className="lunins-card mt-8 p-5 text-sm leading-relaxed text-lunins-muted">
                    <strong className="text-lunins-ink">Nota comercial: </strong>
                    {detalhe.fabricanteReferencia}
                  </p>
                )}
              </>
            ) : (
              <>
                <h2 className="font-display text-xl font-semibold text-lunins-ink md:text-2xl">Informações</h2>
                <p className="mt-6 text-base leading-relaxed text-lunins-muted">{TEXTO_PRODUTO_GENERICO.paragrafo}</p>
              </>
            )}
          </div>

          <div className="lunins-card mx-auto mt-12 max-w-3xl p-6 text-sm leading-relaxed text-lunins-muted md:p-8">
            <strong className="text-lunins-ink">Compra com consultor Lunins:</strong> se você recebeu um link de proposta
            ou campanha institucional, use esse fluxo para fechar com as condições acordadas — incluindo PIX e pagamento
            com cartão via <strong className="text-lunins-ink">InfinitePay</strong> quando disponível na tela do seu
            fechamento. Compras avulsas seguem o atendimento pelo WhatsApp. O catálogo oficial é este site; em dúvidas,
            confira a seção{' '}
            <Link to="/#comprar" className="lunins-link">
              Como comprar
            </Link>{' '}
            na página inicial.
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
